/**
 * MonsterSheetEditor - Authentic D&D Monster Sheet Editor
 *
 * Designed to look and feel like a real D&D monster stat block with:
 * - Parchment texture and fantasy theming
 * - Traditional monster stat block layout
 * - Proper CR and monster-specific fields
 * - D&D-themed styling throughout
 *
 * @date_of_change 2025-09-18 - Created authentic monster sheet editor
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SmartPasteInterface from './SmartPasteInterface/SmartPasteInterface';
import StatBlockViewer from './StatBlockViewer/StatBlockViewer';
import ImageUpload from './ImageUpload/ImageUpload';
import ImageWithFallback from './ImageWithFallback';
import { resolveMonsterArtUrl } from '../services/ArtworkResolver';
import DataService from '../services/DataService';
import CustomAssetManager from '../services/CustomAssetManager';
import './CharacterSheetStyles.css';

const MonsterSheetEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  // State management
  const [monster, setMonster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showSmartPaste, setShowSmartPaste] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    actions: false,
    traits: false,
    advanced: false
  });

  // Load monster data
  useEffect(() => {
    const loadMonster = async () => {
      try {
        setLoading(true);
        const template = await DataService.getMonsterData(templateId);
        setMonster(template);
      } catch (error) {
        console.error('Failed to load monster:', error);
        setStatusMessage('❌ Failed to load monster template');
      } finally {
        setLoading(false);
      }
    };

    if (templateId) {
      loadMonster();
    }
  }, [templateId]);

  // Helper functions
  const updateMonster = (field, value) => {
    setMonster(prev => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const calculateModifier = (score) => {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  // Smart paste handlers
  const handleSmartPasteContent = (parsedContent, suggestions) => {
    console.log('[MonsterSheetEditor] Smart paste content parsed:', parsedContent);
  };

  const handleSmartPasteApply = (changeRecord) => {
    const { field, action, data } = changeRecord;

    try {
      switch (field) {
        case 'actions':
          if (action === 'append') {
            const currentActions = monster.actions || [];
            updateMonster('actions', [...currentActions, data]);
            setStatusMessage(`✅ Added action: ${typeof data === 'object' ? data.name : data}`);
          }
          break;
        case 'traits':
          if (action === 'append') {
            const currentTraits = monster.traits || [];
            updateMonster('traits', [...currentTraits, data]);
            setStatusMessage(`✅ Added trait: ${typeof data === 'object' ? data.name : data}`);
          }
          break;
        case 'abilities':
          if (action === 'merge') {
            updateMonster('abilities', { ...monster.abilities, ...data });
            setStatusMessage(`✅ Updated ability scores`);
          }
          break;
        case 'all':
          if (action === 'replace') {
            const confirmReplace = window.confirm(
              'This will replace all monster data with the parsed content. Are you sure?'
            );
            if (confirmReplace) {
              const mergedData = { ...monster, ...data, id: monster.id };
              setMonster(mergedData);
              setStatusMessage(`✅ Imported complete monster data`);
            }
          }
          break;
        default:
          console.warn('[MonsterSheetEditor] Unknown field in smart paste:', field);
          break;
      }

      setTimeout(() => setStatusMessage(''), 3000);
    } catch (error) {
      console.error('[MonsterSheetEditor] Smart paste apply error:', error);
      setStatusMessage(`❌ Error applying changes: ${error.message}`);
    }
  };

  // Save monster
  const handleSave = async () => {
    if (!monster.name?.trim()) {
      setStatusMessage('❌ Please enter a monster name');
      return;
    }

    try {
      setSaving(true);
      const monsterToSave = {
        ...monster,
        name: monster.name.trim(),
        id: `custom_monster_${monster.name.trim().toLowerCase().replace(/\s+/g, '-')}`,
        isCustom: true,
        lastModified: new Date().toISOString()
      };

      await CustomAssetManager.saveMonster(monsterToSave);

      // @date_of_change: 2025-12-03
      // rule_reference: AGENTS.md SSOT — traditional sheet editor must also feed
      // the canonical monster_template.json entity store via ImportExportService.
      try {
        const { default: ImportExportService } = await import('../services/ImportExportService');
        await ImportExportService.importMonster({
          ...monsterToSave,
          type: 'monster'
        });
      } catch (canonicalError) {
        console.warn('[MonsterSheetEditor] Canonical import via ImportExportService.importMonster failed:', canonicalError);
      }

      setStatusMessage('✅ Monster saved successfully!');

      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Failed to save monster:', error);
      setStatusMessage(`❌ Error saving monster: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="character-sheet-editor" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div style={{ textAlign: 'center', color: '#f4f1e8' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #8B4513',
            borderTop: '4px solid #DAA520',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Loading monster template...</p>
        </div>
      </div>
    );
  }

  if (!monster) {
    return (
      <div className="character-sheet-editor" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div style={{ textAlign: 'center', color: '#f4f1e8' }}>
          <h2>Monster Not Found</h2>
          <p>The requested monster template could not be loaded.</p>
          <button onClick={() => navigate('/')} className="sheet-btn sheet-btn-primary">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="character-sheet-editor">
      {/* Header with action buttons */}
      <div className="sheet-header">
        <h1 className="sheet-title">D&D Monster Stat Block Editor</h1>
        <p className="sheet-subtitle">Create and customize your creatures</p>

        <div className="sheet-action-buttons" style={{ marginTop: '16px' }}>
          <button
            onClick={() => navigate('/')}
            className="sheet-btn sheet-btn-secondary"
          >
            ← Dashboard
          </button>
          <button
            onClick={() => setShowSmartPaste(!showSmartPaste)}
            className={`sheet-btn ${showSmartPaste ? 'sheet-btn-primary' : 'sheet-btn-secondary'}`}
          >
            ✨ Smart Paste
          </button>
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`sheet-btn ${previewMode ? 'sheet-btn-primary' : 'sheet-btn-secondary'}`}
          >
            {previewMode ? '✏️ Edit' : '📋 Stat Block'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="sheet-btn sheet-btn-success"
          >
            {saving ? '⏳ Saving...' : '💾 Save Monster'}
          </button>
        </div>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div style={{
          textAlign: 'center',
          padding: '12px',
          background: statusMessage.includes('✅') ? '#228B22' : '#DC143C',
          color: 'white',
          fontWeight: '600'
        }}>
          {statusMessage}
        </div>
      )}

      {/* Monster Sheet */}
      <div className="character-sheet-page">
        {previewMode ? (
          // @date_of_change: 2025-09-29 - Use traditional D&D stat block format for preview
          <StatBlockViewer monster={monster} />
        ) : (
          <>
            {/* Monster Portrait */}
            <div className="character-portrait-section">
              <div className="portrait-frame">
                {monster.artwork ? (
                  <ImageWithFallback
                    src={resolveMonsterArtUrl(monster)}
                    alt="Monster portrait"
                    className="portrait-image"
                  />
                ) : (
                  <div className="portrait-placeholder">
                    No portrait<br/>uploaded
                  </div>
                )}
              </div>
              <ImageUpload
                currentImage={monster.artwork}
                onImageUploaded={(canonicalKey) => {
                  // @date_of_change: 2025-10-07 - Save to tokenAssetKey (canonical format)
                  updateMonster('tokenAssetKey', canonicalKey);
                }}
                entityName={monster.name || 'custom-monster'}
                entityType="monster"
                disabled={saving}
              />
            </div>

            {/* Monster Name */}
            <div className="character-name-section">
              <input
                type="text"
                value={monster.name || ''}
                onChange={(e) => updateMonster('name', e.target.value)}
                placeholder="Enter monster name"
                className="character-name-input"
              />
            </div>

            {/* Basic Information */}
            <div className="basic-info-row">
              <div className="info-box">
                <div className="info-box-label">Size & Type</div>
                <input
                  type="text"
                  value={`${monster.size || 'Medium'} ${monster.monsterType || monster.type || 'humanoid'}`}
                  onChange={(e) => {
                    const parts = e.target.value.split(' ');
                    const size = parts[0] || 'Medium';
                    const type = parts.slice(1).join(' ') || 'humanoid';
                    updateMonster('size', size);
                    updateMonster('monsterType', type);
                  }}
                  placeholder="Medium humanoid"
                  className="info-box-input"
                />
              </div>
              <div className="info-box">
                <div className="info-box-label">Challenge Rating</div>
                <select
                  value={monster.cr || '1/4'}
                  onChange={(e) => updateMonster('cr', e.target.value)}
                  className="info-box-select"
                >
                  <option value="0">0</option>
                  <option value="1/8">1/8</option>
                  <option value="1/4">1/4</option>
                  <option value="1/2">1/2</option>
                  {[...Array(30)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="info-box">
                <div className="info-box-label">Alignment</div>
                <select
                  value={monster.alignment || 'neutral'}
                  onChange={(e) => updateMonster('alignment', e.target.value)}
                  className="info-box-select"
                >
                  <option value="lawful good">Lawful Good</option>
                  <option value="neutral good">Neutral Good</option>
                  <option value="chaotic good">Chaotic Good</option>
                  <option value="lawful neutral">Lawful Neutral</option>
                  <option value="neutral">Neutral</option>
                  <option value="chaotic neutral">Chaotic Neutral</option>
                  <option value="lawful evil">Lawful Evil</option>
                  <option value="neutral evil">Neutral Evil</option>
                  <option value="chaotic evil">Chaotic Evil</option>
                  <option value="unaligned">Unaligned</option>
                </select>
              </div>
            </div>

            {/* Smart Paste Section */}
            {showSmartPaste && (
              <div className="smart-paste-section">
                <h3 className="smart-paste-title">✨ Smart Content Paste</h3>
                <p className="smart-paste-description">
                  Paste D&D monster content and it will be automatically parsed and added to your stat block
                </p>
                <SmartPasteInterface
                  onContentParsed={handleSmartPasteContent}
                  onApplyChanges={handleSmartPasteApply}
                  context={{
                    entityType: 'monster',
                    currentData: monster
                  }}
                  placeholder="Paste monster actions, traits, or complete stat blocks here..."
                />
              </div>
            )}

            {/* Ability Scores */}
            <div className="ability-scores-section">
              <h2 className="section-title">Ability Scores</h2>
              <div className="ability-scores-grid">
                {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map(ability => (
                  <div key={ability} className="ability-score-box">
                    <div className="ability-name">{ability.slice(0, 3).toUpperCase()}</div>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={monster.abilities?.[ability] || 10}
                      onChange={(e) => updateMonster('abilities', {
                        ...monster.abilities,
                        [ability]: parseInt(e.target.value) || 10
                      })}
                      className="ability-score-input"
                    />
                    <div className="ability-modifier">
                      {calculateModifier(monster.abilities?.[ability] || 10)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Combat Stats */}
            <div className="combat-stats-section">
              <div className="combat-stat-box">
                <div className="combat-stat-label">Armor Class</div>
                <input
                  type="number"
                  min="1"
                  value={monster.ac || 10}
                  onChange={(e) => updateMonster('ac', parseInt(e.target.value) || 10)}
                  className="combat-stat-input"
                />
              </div>
              <div className="combat-stat-box">
                <div className="combat-stat-label">Hit Points</div>
                <input
                  type="number"
                  min="1"
                  value={monster.hp || 1}
                  onChange={(e) => updateMonster('hp', parseInt(e.target.value) || 1)}
                  className="combat-stat-input"
                />
              </div>
              <div className="combat-stat-box">
                <div className="combat-stat-label">Speed</div>
                <input
                  type="number"
                  min="0"
                  value={monster.speed || 30}
                  onChange={(e) => updateMonster('speed', parseInt(e.target.value) || 30)}
                  className="combat-stat-input"
                />
              </div>
              <div className="combat-stat-box">
                <div className="combat-stat-label">Initiative</div>
                <input
                  type="number"
                  value={monster.initiative || 0}
                  onChange={(e) => updateMonster('initiative', parseInt(e.target.value) || 0)}
                  className="combat-stat-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="sheet-collapsible">
              <div
                className="sheet-collapsible-header"
                onClick={() => toggleSection('actions')}
              >
                <div className="sheet-collapsible-title">
                  ⚔️ Actions & Attacks
                </div>
                <div className={`sheet-collapsible-icon ${expandedSections.actions ? 'expanded' : ''}`}>
                  ▶
                </div>
              </div>
              <div className={`sheet-collapsible-content ${expandedSections.actions ? 'expanded' : ''}`}>
                <div className="sheet-text-section">
                  <textarea
                    value={Array.isArray(monster.actions) ? monster.actions.map(a => typeof a === 'object' ? `${a.name}. ${a.description || ''}` : a).join('\n\n') : (monster.actions || '')}
                    onChange={(e) => {
                      const actions = e.target.value.split('\n\n').filter(a => a.trim()).map(actionText => {
                        const lines = actionText.trim().split('\n');
                        if (lines.length > 0) {
                          const firstLine = lines[0];
                          const nameMatch = firstLine.match(/^([^.]+)\./);
                          return {
                            name: nameMatch ? nameMatch[1].trim() : firstLine,
                            description: actionText
                          };
                        }
                        return actionText;
                      });
                      updateMonster('actions', actions);
                    }}
                    placeholder="Enter actions (separate with double line breaks)&#10;Example:&#10;Bite. Melee Weapon Attack: +5 to hit, reach 5 ft., one target. Hit: 7 (1d8+3) piercing damage.&#10;&#10;Claw. Melee Weapon Attack: +5 to hit, reach 5 ft., one target. Hit: 6 (1d6+3) slashing damage."
                    className="sheet-textarea"
                    rows={10}
                  />
                </div>
              </div>
            </div>

            {/* Traits */}
            <div className="sheet-collapsible">
              <div
                className="sheet-collapsible-header"
                onClick={() => toggleSection('traits')}
              >
                <div className="sheet-collapsible-title">
                  🌟 Traits & Special Abilities
                </div>
                <div className={`sheet-collapsible-icon ${expandedSections.traits ? 'expanded' : ''}`}>
                  ▶
                </div>
              </div>
              <div className={`sheet-collapsible-content ${expandedSections.traits ? 'expanded' : ''}`}>
                <div className="sheet-text-section">
                  <textarea
                    value={Array.isArray(monster.traits) ? monster.traits.map(t => typeof t === 'object' ? `${t.name}. ${t.text || t.description || ''}` : t).join('\n\n') : (monster.traits || '')}
                    onChange={(e) => {
                      const traits = e.target.value.split('\n\n').filter(t => t.trim()).map(traitText => {
                        const lines = traitText.trim().split('\n');
                        if (lines.length > 0) {
                          const firstLine = lines[0];
                          const nameMatch = firstLine.match(/^([^.]+)\./);
                          return {
                            name: nameMatch ? nameMatch[1].trim() : firstLine,
                            text: traitText
                          };
                        }
                        return traitText;
                      });
                      updateMonster('traits', traits);
                    }}
                    placeholder="Enter traits (separate with double line breaks)&#10;Example:&#10;Pack Tactics. The wolf has advantage on an attack roll against a creature if at least one of the wolf's allies is within 5 feet of the creature.&#10;&#10;Keen Hearing and Smell. The wolf has advantage on Wisdom (Perception) checks that rely on hearing or smell."
                    className="sheet-textarea"
                    rows={8}
                  />
                </div>
              </div>
            </div>

            {/* Advanced Properties */}
            <div className="sheet-collapsible">
              <div
                className="sheet-collapsible-header"
                onClick={() => toggleSection('advanced')}
              >
                <div className="sheet-collapsible-title">
                  🔧 Resistances & Immunities
                </div>
                <div className={`sheet-collapsible-icon ${expandedSections.advanced ? 'expanded' : ''}`}>
                  ▶
                </div>
              </div>
              <div className={`sheet-collapsible-content ${expandedSections.advanced ? 'expanded' : ''}`}>
                <div className="features-grid">
                  <div className="sheet-text-section">
                    <div className="text-section-title">Damage Resistances</div>
                    <textarea
                      value={Array.isArray(monster.damageResistances) ? monster.damageResistances.join(', ') : (monster.damageResistances || '')}
                      onChange={(e) => updateMonster('damageResistances', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                      placeholder="e.g., cold, necrotic"
                      className="sheet-textarea"
                      rows={3}
                    />
                  </div>
                  <div className="sheet-text-section">
                    <div className="text-section-title">Damage Immunities</div>
                    <textarea
                      value={Array.isArray(monster.damageImmunities) ? monster.damageImmunities.join(', ') : (monster.damageImmunities || '')}
                      onChange={(e) => updateMonster('damageImmunities', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                      placeholder="e.g., poison, fire"
                      className="sheet-textarea"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="sheet-text-section">
                  <div className="text-section-title">Condition Immunities</div>
                  <textarea
                    value={Array.isArray(monster.conditionImmunities) ? monster.conditionImmunities.join(', ') : (monster.conditionImmunities || '')}
                    onChange={(e) => updateMonster('conditionImmunities', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                    placeholder="e.g., charmed, frightened"
                    className="sheet-textarea"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonsterSheetEditor;